/**
 * Your account.
 *
 * A first visit mints a key pair and the server hands back a guest — no sign-up, no email
 * address, nothing to remember. That is the right default for a board game, and it is why
 * the whole handshake is a signature rather than a password.
 *
 * The password exists for exactly one job: reaching the same account from a second
 * device. Registering binds a name and a password to the identity you already have;
 * logging in on a new device binds that device's key to the account, so it only has to be
 * done once per browser.
 */

import { useEffect, useRef, useState } from 'react';

import { MAX_USERNAME } from '@gok/shared';

import { useConnection } from '../net/context.tsx';
import { useUser } from '../net/useUser.tsx';
import { Button, Field, Panel } from '../ui/primitives.tsx';

export function AccountDialog({ open, onClose }: { open: boolean; onClose(): void }) {
  const { userId, send, lastError, clearError } = useConnection();
  const me = useUser(userId);
  const ref = useRef<HTMLDialogElement>(null);

  // Two separate names on purpose: the one shown in the lobby, and the one an account
  // is reached by. They start the same and usually stay the same, but typing in one
  // must not silently rewrite the other.
  const [displayName, setDisplayName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState<'keep' | 'login'>('keep');

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    if (!open || !me) return;
    setDisplayName(me.username);
    setAccountName(me.username);
    clearError();
  }, [open, me, clearError]);

  if (!me) return null;

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className='m-auto w-[min(92vw,26rem)] rounded-lg border border-rule bg-surface
        p-0 text-ink backdrop:bg-black/40'
    >
      <div className='flex flex-col gap-3 p-4'>
        <div className='flex items-baseline justify-between gap-2'>
          <h2 className='font-display text-base font-semibold'>{me.username}</h2>
          <span className='tabular font-mono text-xs text-muted'>
            {Math.round(me.rating.mean)} ± {Math.round(me.rating.rd)}
          </span>
        </div>

        <form
          className='flex items-end gap-2'
          onSubmit={(event) => {
            event.preventDefault();
            clearError();
            const trimmed = displayName.trim();
            if (trimmed && trimmed !== me.username) {
              send({
                t: 'setName',
                username: trimmed,
              });
            }
          }}
        >
          <div className='flex-1'>
            <Field label='Display name'>
              <input
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                maxLength={MAX_USERNAME}
                className='rounded-md border border-rule bg-surface px-2 py-1.5 text-sm'
              />
            </Field>
          </div>
          <Button type='submit'>Rename</Button>
        </form>

        <Panel
          title={me.registered ? 'Sign in elsewhere' : 'Keep this account'}
          className='border-rule'
        >
          <div className='flex flex-col gap-2 p-3'>
            <p className='text-sm text-ink-2'>
              {me.registered
                ? 'This account already has a password. Use it to sign in on another device.'
                : 'You are playing as a guest. This browser remembers you, but nothing else does — set a password to reach this account from anywhere.'}
            </p>

            {!me.registered && (
              <div className='flex gap-1'>
                <Button
                  className='flex-1'
                  tone={mode === 'keep' ? 'primary' : 'quiet'}
                  onClick={() => setMode('keep')}
                >
                  Set a password
                </Button>
                <Button
                  className='flex-1'
                  tone={mode === 'login' ? 'primary' : 'quiet'}
                  onClick={() => setMode('login')}
                >
                  I have an account
                </Button>
              </div>
            )}

            <form
              className='flex flex-col gap-2'
              onSubmit={(event) => {
                event.preventDefault();
                clearError();
                const username = accountName.trim();
                if (!username || !password) return;
                send(
                  me.registered || mode === 'login'
                    ? { t: 'login', username, password }
                    : { t: 'register', username, password },
                );
                setPassword('');
              }}
            >
              <Field
                label={me.registered || mode === 'login'
                  ? 'Account name'
                  : 'Name to keep'}
              >
                <input
                  value={accountName}
                  onChange={(event) => setAccountName(event.target.value)}
                  maxLength={MAX_USERNAME}
                  autoComplete='username'
                  className='rounded-md border border-rule bg-surface px-2 py-1.5 text-sm'
                />
              </Field>
              <Field label='Password'>
                <input
                  type='password'
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  minLength={me.registered || mode === 'login' ? 1 : 8}
                  autoComplete={me.registered || mode === 'login'
                    ? 'current-password'
                    : 'new-password'}
                  className='rounded-md border border-rule bg-surface px-2 py-1.5 text-sm'
                />
              </Field>
              <Button type='submit' tone='primary'>
                {me.registered || mode === 'login' ? 'Sign in' : 'Keep this account'}
              </Button>
            </form>

            {lastError && (
              <p className='text-sm text-player-1'>{explain(lastError.code)}</p>
            )}
          </div>
        </Panel>

        <Button onClick={() => ref.current?.close()} className='self-end'>Close</Button>
      </div>
    </dialog>
  );
}

/** Errors say what to do about it, not what the server called it. */
function explain(code: string): string {
  switch (code) {
    case 'taken':
      return 'That name is already in use. Try another.';
    case 'auth':
      return 'That name and password do not match an account.';
    case 'rateLimit':
      return 'Too many attempts. Wait a moment and try again.';
    default:
      return 'That did not work. Try again.';
  }
}
